import { useCallback, useEffect, useRef, useState } from 'react';

import timeout from 'utils/common/timeout';
import { addInternalNotification, removeInternalNotification } from 'store/reducers/InternalNotificationsSlice';

import useAppSelector from 'hooks/redux/useAppSelector';
import useAppDispatch from 'hooks/redux/useAppDispatch';

import { GOOGLE_CAPTCHA_KEY } from 'config';
import { InAppNotificationItems } from 'constants/internalNotifications';

import { InternalNotificationsTypes, NotificationMessageType } from 'types/internalNotifications';

const GoogleCaptchaUrl = 'https://www.google.com/recaptcha/api.js';

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_INTERVAL = 3000;
const DEFAULT_WAIT_TIMEOUT = 3000;

interface IGoogleCaptchaReloadScriptOptions {
  maxAttempts?: number;
  interval?: number;
  disabled?: boolean;
}

const useGoogleCaptchaReloadScript = (isCaptchaReady: boolean, options?: IGoogleCaptchaReloadScriptOptions) => {
  const { maxAttempts = DEFAULT_MAX_ATTEMPTS, interval = DEFAULT_INTERVAL, disabled } = options || {};

  const [isReloadCompleted, setIsReloadCompleted] = useState(false);
  const [isReadyToReload, setIsReadyToReload] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const intervalId = useRef<NodeJS.Timeout | null>(null);

  const { inAppNotifications } = useAppSelector(state => state.internalNotifications);

  const reloadReCaptchaScript = useCallback(() => {
    setAttempts(prev => {
      console.log('Reload Captcha', `Attempt #${prev + 1}`);

      return prev + 1;
    });

    const existingScript = document.querySelector(`script[src*="${GoogleCaptchaUrl}"]`);

    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = `${GoogleCaptchaUrl}?render=${GOOGLE_CAPTCHA_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const waitBeforeReload = async () => {
      await timeout(DEFAULT_WAIT_TIMEOUT);
      setIsReadyToReload(true);
    };

    if (!disabled) {
      waitBeforeReload();
    }

    return () => {
      console.log('Reload Captcha', `Completed (clear interval ${intervalId.current})`);

      clearInterval(intervalId.current);
    };
  }, [disabled]);

  useEffect(() => {
    const reload = async () => {
      console.log('Reload Captcha', `Checking captcha for reload... `);

      const isCaptchaReloadCompleted = isCaptchaReady || isReloadCompleted || attempts >= maxAttempts;

      if (isCaptchaReloadCompleted && intervalId.current) {
        console.log('Reload Captcha', `Completed (clear interval ${intervalId.current})`);

        clearInterval(intervalId.current);
        intervalId.current = null;
      }

      if (isCaptchaReloadCompleted) {
        setIsReloadCompleted(true);

        if (isCaptchaReady) {
          console.log('Reload Captcha', `Completed (Captcha is READY)`);
        } else {
          console.error('Reload Captcha', `Completed (Captcha is FAILED)`);
        }

        return;
      }

      if (!isReloadCompleted && !intervalId.current) {
        console.log('Reload Captcha', `Initiate interval for reload`);

        const newIntervalId = setInterval(() => {
          reloadReCaptchaScript();
        }, interval);

        intervalId.current = newIntervalId;
      }
    };

    if (isReadyToReload && !disabled) {
      reload();
    }
  }, [
    disabled,
    isReadyToReload,
    isCaptchaReady,
    attempts,
    interval,
    isReloadCompleted,
    maxAttempts,
    reloadReCaptchaScript,
  ]);

  const isCaptchaInitFailed = isReloadCompleted && !isCaptchaReady;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (disabled) {
      return;
    }

    const isNotificationExists = inAppNotifications?.find(
      item => item.id === NotificationMessageType.EXTERNAL_RESOURCES_FAILED,
    );

    if (isCaptchaInitFailed || (attempts > 0 && !isCaptchaReady)) {
      if (isNotificationExists) {
        return;
      }

      const item = InAppNotificationItems[NotificationMessageType.EXTERNAL_RESOURCES_FAILED];
      dispatch(addInternalNotification({ item, type: InternalNotificationsTypes.IN_APP }));
      return;
    }

    if (isCaptchaReady && isNotificationExists) {
      setAttempts(0);

      dispatch(
        removeInternalNotification({
          id: NotificationMessageType.EXTERNAL_RESOURCES_FAILED,
          type: InternalNotificationsTypes.IN_APP,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isCaptchaInitFailed, isCaptchaReady, attempts, disabled]);

  return { isCaptchaReady, isReloadCompleted, attempts };
};

export default useGoogleCaptchaReloadScript;
